import { useEffect, useState } from "react";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy,
  CheckCircle2,
  Users,
  Gift,
  Share2,
  Sparkles,
  Mail,
  MessageSquare,
  Twitter,
} from "lucide-react";

export default function Refer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/referrals/me");
        setData(r.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const shareUrl = data ? `${window.location.origin}/register?ref=${data.code}` : "";
  const shareText = data
    ? `I've been using Aura Credit to actually get bad stuff off my credit report. Try it free — you get all the AI dispute-letter tools, I get a month of Plus. ${shareUrl}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.code);
      toast.success(`Code "${data.code}" copied`);
    } catch { /* empty */ }
  };

  const shareVia = {
    twitter: () =>
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
        "_blank",
        "noopener,noreferrer"
      ),
    email: () =>
      (window.location.href = `mailto:?subject=${encodeURIComponent("Something that actually fixes credit")}&body=${encodeURIComponent(shareText)}`),
    sms: () => (window.location.href = `sms:?&body=${encodeURIComponent(shareText)}`),
    native: async () => {
      if (navigator.share) {
        try {
          await navigator.share({ title: "Aura Credit", text: shareText, url: shareUrl });
        } catch { /* user cancelled */ }
      } else {
        copyLink();
      }
    },
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Invite friends</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Give a friend Aura.
          <br />
          <span className="text-[#D5FF3F]">Get a month of Plus.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl leading-relaxed">
          For every friend who subscribes to any Aura package via your link, we credit you 30 days
          of Aura Plus &mdash; unlimited certified mail, Mortgage Sim, CFPB Escalation, ID-Theft
          Kit, everything. Stackable, no cap.
        </p>
      </div>

      {loading ? (
        <div className="text-[#A8ADA5]">Loading…</div>
      ) : (
        <>
          {/* Hero share card */}
          <div
            className="card-surface p-8 md:p-12 mb-8 relative overflow-hidden"
            data-testid="refer-hero"
          >
            <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D5FF3F]/15 blur-[120px] pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 border border-[#D5FF3F]/30 bg-[#D5FF3F]/5 rounded-full px-3 py-1.5 mb-6">
                <Gift size={12} className="text-[#D5FF3F]" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                  +{data.reward_per_signup_days} days of Plus per signup
                </span>
              </div>

              <div className="mb-6">
                <div className="overline mb-2">Your share link</div>
                <div className="flex gap-2 flex-wrap">
                  <Input
                    readOnly
                    value={shareUrl}
                    data-testid="refer-share-url"
                    onClick={(e) => e.target.select()}
                    className="flex-1 min-w-0 rounded-sm font-mono text-sm bg-[#0B0C0E] border-[#26282C]"
                  />
                  <Button
                    onClick={copyLink}
                    data-testid="refer-copy-link-btn"
                    className="rounded-full btn-lime h-11 px-5"
                  >
                    {copied ? (
                      <><CheckCircle2 size={14} className="mr-1.5" /> Copied</>
                    ) : (
                      <><Copy size={14} className="mr-1.5" /> Copy link</>
                    )}
                  </Button>
                </div>
              </div>

              <div className="mb-6">
                <div className="overline mb-2">Or use your code</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={copyCode}
                    data-testid="refer-copy-code-btn"
                    className="ticker text-3xl md:text-4xl text-[#D5FF3F] hover:opacity-80 transition-opacity"
                  >
                    {data.code}
                  </button>
                  <span className="text-xs font-mono uppercase tracking-widest text-[#6D716C]">
                    Tap to copy
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={shareVia.native}
                  data-testid="share-native"
                  className="rounded-full btn-outline h-10 px-4"
                >
                  <Share2 size={13} className="mr-1.5" /> Share
                </Button>
                <Button
                  onClick={shareVia.twitter}
                  data-testid="share-twitter"
                  className="rounded-full btn-outline h-10 px-4"
                >
                  <Twitter size={13} className="mr-1.5" /> Post
                </Button>
                <Button
                  onClick={shareVia.email}
                  data-testid="share-email"
                  className="rounded-full btn-outline h-10 px-4"
                >
                  <Mail size={13} className="mr-1.5" /> Email
                </Button>
                <Button
                  onClick={shareVia.sms}
                  data-testid="share-sms"
                  className="rounded-full btn-outline h-10 px-4"
                >
                  <MessageSquare size={13} className="mr-1.5" /> Text
                </Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="card-surface p-5" data-testid="stat-invited">
              <div className="overline mb-2">Friends invited</div>
              <div className="ticker text-4xl text-[#F5F3EE]">{data.invited_count}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                signed up
              </div>
            </div>
            <div className="card-surface p-5" data-testid="stat-paid-invited">
              <div className="overline mb-2">Subscribed</div>
              <div className="ticker text-4xl text-[#D5FF3F]">{data.paid_invited_count || 0}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                paid — reward earned
              </div>
            </div>
            <div className="card-surface p-5" data-testid="stat-earned">
              <div className="overline mb-2">Plus earned</div>
              <div className="ticker text-4xl text-[#7CFF9B]">
                {data.rewards_earned_days}
                <span className="text-[#6D716C] text-xl"> d</span>
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                days credited
              </div>
            </div>
            <div className="card-surface p-5" data-testid="stat-until">
              <div className="overline mb-2">Plus active until</div>
              <div className="ticker text-2xl text-[#F5F3EE]">
                {data.pro_until
                  ? new Date(data.pro_until).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "—"}
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-1">
                stackable — no cap
              </div>
            </div>
          </div>

          {/* Invited list */}
          {data.invited?.length > 0 && (
            <div className="card-surface p-6" data-testid="invited-list">
              <div className="flex items-center gap-2 mb-4">
                <Users size={14} className="text-[#A8ADA5]" />
                <div className="overline">Your invites</div>
              </div>
              <div className="divide-y divide-[#26282C]">
                {data.invited.map((f, i) => (
                  <div key={i} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#D5FF3F]/15 flex items-center justify-center">
                        <span className="text-xs font-medium text-[#D5FF3F]">
                          {(f.name || "F").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium">{f.name}</div>
                        <div className="text-xs text-[#6D716C] font-mono">
                          Joined {new Date(f.joined_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    {f.paid ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F]/15 text-[#D5FF3F]">
                        +{data.reward_per_signup_days}d Plus earned
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#26282C] text-[#A8ADA5]">
                        Pending · subscribes to unlock
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How it works */}
          <div className="card-surface p-6 mt-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-[#D5FF3F]" />
              <div className="overline">How it works</div>
            </div>
            <ol className="space-y-3">
              {[
                "Share your link (or code) with a friend who's fighting their credit.",
                "They sign up at auracleancredit.com/register?ref=" + (data.code || "…"),
                "The moment they subscribe to Aura Basic or Plus, you get +30 days of Aura Plus added on top of what you have.",
                "They start with the same $29.99/mo or $49.99/mo pricing everyone else gets — no risk to them.",
                "No cap. Ten paying friends = ten months of Plus.",
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#A8ADA5]">
                  <span className="ticker text-xs text-[#D5FF3F] flex-shrink-0 mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </AppShell>
  );
}
